const DEFAULT_CPU_PARTITION_SPECS = {
  max_workers: 10,
  max_cpus: 60,
  max_mem_gi: 240,
};

const CPU_PARTITION_NAME = "CPU";

const DEFAULT_GPU_PARTITION_SPECS = {
  max_workers: 1,
  max_cpus: 4,
  max_gpus: 1,
  max_mem_gi: 120,
  has_burst_buffer: false,
};

const KIB_PER_GIB = 1024 * 1024;

const DECIMAL_INTEGER_REGEX = /^[0-9]+$/;

export const ParsePositiveIntInput = (value) => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!DECIMAL_INTEGER_REGEX.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
};

const validatePositiveIntInput = (value, fieldName, max = null) => {
  if (typeof value === "string" && !value.trim()) {
    return `Empty or invalid ${fieldName}.`;
  }
  const num = ParsePositiveIntInput(value);
  if (num === null) {
    return `Invalid ${fieldName}. Must be a positive integer.`;
  }

  if (max !== null && max !== undefined) {
    const maxNum =
      typeof max === "number" && Number.isFinite(max)
        ? Math.floor(max)
        : ParsePositiveIntInput(max);
    if (!Number.isFinite(maxNum) || maxNum < 0) {
      return `Invalid ${fieldName} maximum. Must be a positive integer.`;
    }
    if (maxNum === 0) {
      return `No capacity available for ${fieldName.toLowerCase()}.`;
    }
    if (num > maxNum) {
      return `${fieldName} cannot exceed ${maxNum}.`;
    }
  }
  return "";
};

export const validateWalltimeHours = (value, maxWalltime) =>
  validatePositiveIntInput(value, "Walltime", maxWalltime);

const normalizePartitionName = (value) => (value || "").trim().toLowerCase();

export const isCpuPartitionName = (partitionName) =>
  normalizePartitionName(partitionName) ===
  normalizePartitionName(CPU_PARTITION_NAME);

export const getAllocationByPartitionName = (selectedGroup, partitionName) => {
  if (!partitionName || !selectedGroup) return null;
  return (selectedGroup.allocations || []).find(
    (allocation) =>
      normalizePartitionName(allocation.partition_name) ===
      normalizePartitionName(partitionName),
  );
};

export const getTargetAllocation = (selectedGroup, gpuProduct = null) =>
  getAllocationByPartitionName(selectedGroup, gpuProduct || CPU_PARTITION_NAME);

export const getTargetQueueName = (
  selectedGroup,
  gpuProduct = null,
  isBatch = false,
) => {
  const allocation = getTargetAllocation(selectedGroup, gpuProduct);
  if (!allocation) return null;
  return isBatch
    ? allocation.batch_local_queue || null
    : allocation.interactive_local_queue || null;
};

const validateGpuProductInput = (
  gpuProduct,
  selectedGPUProduct,
  selectedGroupName,
  accessibleGPUs,
) => {
  if (accessibleGPUs?.length === 0) {
    return `No GPU partitions are accessible in the selected group: ${selectedGroupName}.`;
  } else if (!!gpuProduct && !selectedGPUProduct) {
    const matchingGpu = accessibleGPUs.find((gpu) => gpu.type === gpuProduct);
    return matchingGpu
      ? "This GPU product is currently unavailable."
      : `This GPU product is not accessible by ${selectedGroupName}.`;
  }
};

export const validateDnsLabel = (value) => {
  const trimmedValue = value ?? "";
  const hasValue = !!trimmedValue.length;
  const errors = {
    length: trimmedValue.length > 63,
    format:
      (hasValue && !/^[a-z0-9-]*$/.test(trimmedValue)) ||
      (hasValue && !/^[a-z0-9](.*[a-z0-9])?$/.test(trimmedValue)),
  };
  return {
    errors,
    isValid: !!trimmedValue.trim() && !errors.length && !errors.format,
  };
};

export const getCpuPartitionSpecs = (selectedGroup) => {
  if (!selectedGroup) return DEFAULT_CPU_PARTITION_SPECS;
  const alloc = getTargetAllocation(selectedGroup);
  if (!alloc) return DEFAULT_CPU_PARTITION_SPECS;
  return {
    max_workers: alloc.max_nodes ?? DEFAULT_CPU_PARTITION_SPECS.max_workers,
    max_cpus: alloc.max_cpus_per_node ?? DEFAULT_CPU_PARTITION_SPECS.max_cpus,
    max_mem_gi:
      alloc.max_mem_gi_per_node ?? DEFAULT_CPU_PARTITION_SPECS.max_mem_gi,
  };
};

export const getGpuPartitionSpecs = (selectedGroup, gpuProduct) => {
  if (!gpuProduct || !selectedGroup) return DEFAULT_GPU_PARTITION_SPECS;
  const alloc = getTargetAllocation(selectedGroup, gpuProduct);
  if (!alloc) return DEFAULT_GPU_PARTITION_SPECS;
  return {
    max_workers: alloc.max_nodes ?? DEFAULT_GPU_PARTITION_SPECS.max_workers,
    max_cpus: alloc.max_cpus_per_node ?? DEFAULT_GPU_PARTITION_SPECS.max_cpus,
    max_gpus: alloc.max_gpus_per_node ?? DEFAULT_GPU_PARTITION_SPECS.max_gpus,
    max_mem_gi:
      alloc.max_mem_gi_per_node ?? DEFAULT_GPU_PARTITION_SPECS.max_mem_gi,
    has_burst_buffer:
      alloc.has_burst_buffer ?? DEFAULT_GPU_PARTITION_SPECS.has_burst_buffer,
  };
};

const floorOrNull = (value) => {
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Math.floor(value));
};

const divideFloorOrNull = (dividend, divisor) => {
  if (!Number.isFinite(dividend)) return null;
  if (!Number.isFinite(divisor) || divisor <= 0) return 0;
  return Math.max(0, Math.floor(dividend / divisor));
};

const minDefined = (...values) => {
  const numericValues = values.filter((value) => Number.isFinite(value));
  if (numericValues.length === 0) {
    return null;
  }
  return Math.min(...numericValues);
};

const clampLimit = (fallbackValue, ...candidates) => {
  const bounded = minDefined(fallbackValue, ...candidates);
  return bounded ?? fallbackValue;
};

export const getCpuEffectiveLimits = (selectedGroup, queueQuota, config) => {
  const partitionSpecs = getCpuPartitionSpecs(selectedGroup);
  const requestedNodeCount = ParsePositiveIntInput(config?.nodeCount) ?? 1;
  const requestedCpuCores = ParsePositiveIntInput(config?.cpuCores) ?? 1;
  const requestedMemoryGi = ParsePositiveIntInput(config?.memory) ?? 1;

  const availableCpuCores = floorOrNull(
    (queueQuota?.cpu_millicores ?? Number.POSITIVE_INFINITY) / 1000,
  );
  const availableMemoryGi = floorOrNull(
    (queueQuota?.memory_kibibytes ?? Number.POSITIVE_INFINITY) / KIB_PER_GIB,
  );

  const maxWorkers = clampLimit(
    partitionSpecs.max_workers,
    divideFloorOrNull(availableCpuCores, requestedCpuCores),
    divideFloorOrNull(availableMemoryGi, requestedMemoryGi),
  );
  const maxCpus = clampLimit(
    partitionSpecs.max_cpus,
    divideFloorOrNull(availableCpuCores, requestedNodeCount),
  );
  const maxMemGi = clampLimit(
    partitionSpecs.max_mem_gi,
    divideFloorOrNull(availableMemoryGi, requestedNodeCount),
  );

  return {
    ...partitionSpecs,
    max_workers: maxWorkers,
    max_cpus: maxCpus,
    max_mem_gi: maxMemGi,
  };
};

export const getGpuEffectiveLimits = (
  selectedGroup,
  gpuProduct,
  queueQuota,
  config,
) => {
  const partitionSpecs = getGpuPartitionSpecs(selectedGroup, gpuProduct);
  const requestedNodeCount = ParsePositiveIntInput(config?.nodeCount) ?? 1;
  const requestedGpuCount = ParsePositiveIntInput(config?.gpuCount) ?? 1;
  const requestedMemoryGi = ParsePositiveIntInput(config?.memory) ?? 1;
  const requestedCpuCores =
    deriveGpuCpuCores(requestedGpuCount, partitionSpecs) || 1;

  const availableGpuCount = floorOrNull(
    queueQuota?.gpu_count ?? Number.POSITIVE_INFINITY,
  );
  const availableCpuCores = floorOrNull(
    (queueQuota?.cpu_millicores ?? Number.POSITIVE_INFINITY) / 1000,
  );
  const availableMemoryGi = floorOrNull(
    (queueQuota?.memory_kibibytes ?? Number.POSITIVE_INFINITY) / KIB_PER_GIB,
  );

  const maxWorkers = clampLimit(
    partitionSpecs.max_workers,
    divideFloorOrNull(availableGpuCount, requestedGpuCount),
    divideFloorOrNull(availableCpuCores, requestedCpuCores),
    divideFloorOrNull(availableMemoryGi, requestedMemoryGi),
  );
  const maxGpus = clampLimit(
    partitionSpecs.max_gpus,
    divideFloorOrNull(availableGpuCount, requestedNodeCount),
  );
  const maxCpus = clampLimit(
    partitionSpecs.max_cpus,
    divideFloorOrNull(availableCpuCores, requestedNodeCount),
  );
  const maxMemGi = clampLimit(
    partitionSpecs.max_mem_gi,
    divideFloorOrNull(availableMemoryGi, requestedNodeCount),
  );

  return {
    ...partitionSpecs,
    max_workers: maxWorkers,
    max_cpus: maxCpus,
    max_gpus: maxGpus,
    max_mem_gi: maxMemGi,
  };
};

export const deriveGpuCpuCores = (gpuCount, partitionSpecs) => {
  const count = Number(gpuCount);
  if (!Number.isInteger(count) || count <= 0) return 0;
  const { max_cpus, max_gpus } = partitionSpecs || DEFAULT_GPU_PARTITION_SPECS;
  if (!max_gpus) return 0;
  return Math.max(0, Math.floor((count * max_cpus) / max_gpus));
};

export const validateCpuConfig = (
  config,
  { selectedGroup, effectiveLimits } = {},
) => {
  const partitionSpecs = effectiveLimits || getCpuPartitionSpecs(selectedGroup);
  const errors = {
    nodeCount: validatePositiveIntInput(
      config?.nodeCount,
      "Number of workers",
      partitionSpecs.max_workers,
    ),
    cpuCores: validatePositiveIntInput(
      config?.cpuCores,
      "vCPUs",
      partitionSpecs.max_cpus,
    ),
    memory: validatePositiveIntInput(
      config?.memory,
      "Memory size (Gi)",
      partitionSpecs.max_mem_gi,
    ),
  };

  return {
    errors,
    isValid: !errors.nodeCount && !errors.cpuCores && !errors.memory,
    partitionSpecs,
  };
};

export const validateGpuConfig = (
  config,
  { selectedGroup, selectedGPUProduct, accessibleGPUs, effectiveLimits } = {},
) => {
  const partitionSpecs =
    effectiveLimits || getGpuPartitionSpecs(selectedGroup, config?.gpuProduct);

  const hasValidGpuProduct = !!config?.gpuProduct && !!selectedGPUProduct;
  const errors = {
    gpuProduct: validateGpuProductInput(
      config?.gpuProduct,
      selectedGPUProduct,
      selectedGroup?.name,
      accessibleGPUs,
    ),
    gpuCount: hasValidGpuProduct
      ? validatePositiveIntInput(
          config?.gpuCount,
          "GPU count",
          partitionSpecs.max_gpus,
        )
      : "",
    nodeCount: hasValidGpuProduct
      ? validatePositiveIntInput(
          config?.nodeCount,
          "Number of workers",
          partitionSpecs.max_workers,
        )
      : "",
    memory: hasValidGpuProduct
      ? validatePositiveIntInput(
          config?.memory,
          "Memory size (Gi)",
          partitionSpecs.max_mem_gi,
        )
      : "",
  };

  return {
    errors,
    isValid:
      hasValidGpuProduct &&
      !errors.gpuProduct &&
      !errors.nodeCount &&
      !errors.gpuCount &&
      !errors.memory,
    partitionSpecs,
  };
};
